import { ShieldQuestion, UserPlus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@shared/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@shared/contexts';
import { useBotContact, openWhatsAppChat } from '@shared/lib/api/hooks/useValidation';

interface UnclaimedBannerProps {
  professionalName: string;
}

/**
 * Banner displayed on unclaimed professional profiles.
 * Informs visitors the profile is unverified and provides
 * options for the real professional to claim or request removal.
 *
 * Flow logic:
 * - Not logged in: redirect to registration with info toast
 * - Logged in + already has professional profile: show error (not eligible)
 * - Logged in + no professional profile: redirect to WhatsApp bot with claiming intent
 */
export function UnclaimedBanner({ professionalName }: UnclaimedBannerProps) {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { data: botContact } = useBotContact();

  // Check if user has a professional profile linked
  // API returns professionalId field, not isProfessional boolean
  const userHasProfessionalProfile = Boolean(
    user?.isProfessional || (user as { professionalId?: string })?.professionalId
  );

  const handleClaimProfile = () => {
    // Case 1: Not logged in - redirect to register
    if (!isAuthenticated) {
      toast.info(
        'Para reclamar el perfil, creá una cuenta primero. Asegurate de crear la cuenta desde el teléfono al que te llegaron contactos de ProFessionals.',
        { duration: 10000 }
      );
      void navigate('/register');
      return;
    }

    // Case 2: Logged in but already has a professional profile
    if (userHasProfessionalProfile) {
      toast.error('Ya tenés un perfil profesional asociado a tu cuenta. No podés reclamar otro perfil.', {
        duration: 8000
      });
      return;
    }

    // Case 3: Logged in without professional profile - redirect to WhatsApp bot
    if (botContact) {
      const message = `Hola, quiero reclamar el perfil de ${professionalName}`;
      openWhatsAppChat(botContact, message);
    } else {
      toast.error('No se pudo obtener el contacto del bot. Intenta de nuevo.');
    }
  };

  const handleRemovalRequest = () => {
    // Case 1: Not logged in - redirect to register
    if (!isAuthenticated) {
      toast.info(
        'Para solicitar la eliminación, creá una cuenta primero. Asegurate de crear la cuenta desde el teléfono al que te llegaron contactos de ProFessionals.',
        { duration: 10000 }
      );
      void navigate('/register');
      return;
    }

    // Case 2: Logged in but already has a professional profile
    if (userHasProfessionalProfile) {
      toast.error(
        'Ya tenés un perfil profesional asociado a tu cuenta. No podés solicitar eliminación de otro perfil.',
        { duration: 8000 }
      );
      return;
    }

    // Case 3: Logged in without professional profile - redirect to WhatsApp bot
    if (botContact) {
      const message = `Hola, quiero solicitar la eliminación del perfil de ${professionalName}`;
      openWhatsAppChat(botContact, message);
    } else {
      toast.error('No se pudo obtener el contacto del bot. Intenta de nuevo.');
    }
  };

  return (
    <div className='relative overflow-hidden rounded-lg bg-white shadow-md border border-[#CECECE]/60'>
      {/* Subtle diagonal pattern background */}
      <div
        className='absolute inset-0 opacity-[0.03] pointer-events-none'
        style={{
          backgroundImage: `repeating-linear-gradient(
            -45deg,
            #00796B,
            #00796B 1px,
            transparent 1px,
            transparent 8px
          )`
        }}
      />

      <div className='relative flex flex-col sm:flex-row items-start gap-4 p-4'>
        {/* Icon container with subtle glow */}
        <div className='flex-shrink-0'>
          <div
            className='flex items-center justify-center w-11 h-11 rounded-full'
            style={{
              background: 'linear-gradient(135deg, rgba(26, 192, 198, 0.12) 0%, rgba(0, 121, 107, 0.12) 100%)',
              boxShadow: '0 0 0 1px rgba(0, 121, 107, 0.15)'
            }}
          >
            <ShieldQuestion className='w-5 h-5' style={{ color: '#00796B' }} strokeWidth={1.75} />
          </div>
        </div>

        {/* Content */}
        <div className='flex-1 min-w-0'>
          <p className='text-sm leading-relaxed' style={{ color: '#4F4F4F' }}>
            Este perfil es informativo y aún no fue reclamado por el profesional. La información mostrada puede no estar
            actualizada.
          </p>

          {/* Actions */}
          <div className='flex flex-wrap gap-2.5 mt-4'>
            <Button
              size='sm'
              className='gap-2 font-medium shadow-sm'
              style={{
                background: 'linear-gradient(135deg, #00796B 0%, #005D52 100%)'
              }}
              onClick={handleClaimProfile}
            >
              <UserPlus className='w-4 h-4' />
              Reclamar perfil
            </Button>

            <Button
              variant='outline'
              size='sm'
              className='gap-2 font-medium border-[#CECECE] text-[#4F4F4F] hover:bg-[#f2f2f2] hover:text-[#333333] hover:border-[#00796B]/30'
              onClick={handleRemovalRequest}
            >
              <Trash2 className='w-3.5 h-3.5' />
              Solicitar eliminación
            </Button>
          </div>
        </div>
      </div>

      {/* Bottom subtle line accent */}
      <div
        className='h-px w-full'
        style={{
          background: 'linear-gradient(90deg, #1AC0C6 0%, transparent 50%)',
          opacity: 0.3
        }}
      />
    </div>
  );
}

export default UnclaimedBanner;
